import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { Crown, Plus, Trash2, Save } from 'lucide-react';

export function ProviderLoyaltySettings({ 
  data, 
  setData, 
  isSaving, 
  onSave,
  addTier,
  updateTier,
  removeTier
}) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-bold text-slate-900 font-[Poppins]">Provider Loyalty Program</h3>
          <p className="text-sm text-slate-500 mt-1">
            Incentivize providers to stay on the platform with rewards and tier benefits
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-sm font-medium ${
          data.enabled 
            ? 'bg-green-100 text-green-700' 
            : 'bg-stone-100 text-stone-500'
        }`}>
          {data.enabled ? 'Program Active' : 'Program Inactive'}
        </div>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mb-6">
        <div>
          <p className="font-medium text-slate-900">Enable Provider Loyalty Program</p>
          <p className="text-sm text-slate-500">
            Reward providers for completing jobs and maintaining quality
          </p>
        </div>
        <Switch
          checked={data.enabled}
          onCheckedChange={(checked) => setData(prev => ({ ...prev, enabled: checked }))}
          data-testid="provider-loyalty-toggle"
        />
      </div>

      <div className="space-y-6">
        {/* Loyalty Tiers */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-base font-semibold">Loyalty Tiers</Label>
            <Button variant="outline" size="sm" onClick={addTier}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Tier
            </Button>
          </div>
          <div className="space-y-3">
            {data.tiers.map((tier, index) => (
              <div key={tier.name || `tier-${index}`} className={`p-4 border rounded-xl ${
                tier.badge_color === 'bronze' ? 'bg-amber-50/50 border-amber-200' :
                tier.badge_color === 'silver' ? 'bg-slate-50 border-slate-300' :
                tier.badge_color === 'gold' ? 'bg-yellow-50 border-yellow-300' :
                tier.badge_color === 'platinum' ? 'bg-purple-50 border-purple-200' :
                'bg-white border-slate-200'
              }`}>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Tier Name</Label>
                    <Input
                      value={tier.name}
                      onChange={(e) => updateTier(index, 'name', e.target.value)}
                      className="h-9 mt-1"
                      data-testid={`tier-name-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Min Jobs</Label>
                    <Input
                      type="number"
                      value={tier.min_jobs}
                      onChange={(e) => updateTier(index, 'min_jobs', parseInt(e.target.value) || 0)}
                      className="h-9 mt-1"
                      data-testid={`tier-jobs-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Commission Discount %</Label>
                    <Input
                      type="number"
                      value={tier.discount_percentage}
                      onChange={(e) => updateTier(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                      className="h-9 mt-1"
                      data-testid={`tier-discount-${index}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <Switch
                      checked={tier.priority_booking}
                      onCheckedChange={(checked) => updateTier(index, 'priority_booking', checked)}
                    />
                    <span className="text-xs text-slate-600">Priority</span>
                  </div>
                  <div className="flex items-center justify-end pt-4">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => removeTier(index)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Points & Bonuses */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Points per Completed Job</Label>
            <Input
              type="number"
              step="0.1"
              value={data.booking_multiplier}
              onChange={(e) => setData(prev => ({ ...prev, booking_multiplier: parseFloat(e.target.value) || 1 }))}
              className="h-12 mt-1.5"
              data-testid="loyalty-multiplier"
            />
            <p className="text-xs text-slate-500 mt-1">Multiplier for points calculation</p>
          </div>
          <div>
            <Label>Rating Bonus (4.5+ stars)</Label>
            <Input
              type="number"
              value={data.rating_bonus}
              onChange={(e) => setData(prev => ({ ...prev, rating_bonus: parseInt(e.target.value) || 0 }))}
              className="h-12 mt-1.5"
              data-testid="loyalty-rating-bonus"
            />
            <p className="text-xs text-slate-500 mt-1">Bonus points for high-rated providers</p>
          </div>
          <div>
            <Label>Completion Bonus (90%+ rate)</Label>
            <Input
              type="number"
              value={data.completion_bonus}
              onChange={(e) => setData(prev => ({ ...prev, completion_bonus: parseInt(e.target.value) || 0 }))}
              className="h-12 mt-1.5"
              data-testid="loyalty-completion-bonus"
            />
            <p className="text-xs text-slate-500 mt-1">Bonus for high completion rate</p>
          </div>
          <div>
            <Label>Monthly Jobs for Bonus</Label>
            <Input
              type="number"
              value={data.monthly_bonus_threshold}
              onChange={(e) => setData(prev => ({ ...prev, monthly_bonus_threshold: parseInt(e.target.value) || 20 }))}
              className="h-12 mt-1.5"
              data-testid="loyalty-monthly-threshold"
            />
            <p className="text-xs text-slate-500 mt-1">Jobs required for monthly bonus</p>
          </div>
        </div>

        <div>
          <Label>Monthly Bonus Amount (Php)</Label>
          <Input
            type="number"
            value={data.monthly_bonus_amount}
            onChange={(e) => setData(prev => ({ ...prev, monthly_bonus_amount: parseInt(e.target.value) || 500 }))}
            className="h-12 mt-1.5"
            data-testid="loyalty-monthly-amount"
          />
        </div>

        <div>
          <Label>Program Description</Label>
          <textarea
            placeholder="Describe the loyalty program..."
            value={data.description}
            onChange={(e) => setData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full h-24 px-4 py-3 mt-1.5 border border-stone-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="loyalty-description"
          />
        </div>

        <div className="p-4 bg-purple-50 rounded-xl">
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-900">How Provider Loyalty Works</p>
              <ul className="text-sm text-purple-700 mt-2 space-y-1">
                <li>1. Providers earn points for every completed job</li>
                <li>2. Higher tiers unlock commission discounts (keep more earnings)</li>
                <li>3. Priority booking gives providers first access to new jobs</li>
                <li>4. Monthly bonuses reward consistent activity</li>
                <li>5. This keeps providers engaged and reduces off-platform transactions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <Button 
        onClick={onSave}
        disabled={isSaving}
        className="mt-6 bg-slate-900 hover:bg-slate-800"
        data-testid="save-provider-loyalty-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {isSaving ? 'Saving...' : 'Save Provider Loyalty Settings'}
      </Button>
    </div>
  );
}
